import { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { TokenFail } from '@/components/common/TokenFail';
import { PopupType, UserState } from '@/types';
import { useUserState } from '@/hooks/useUserState';
import { ctxUser, createUserContextValue } from '@/contexts/UserContext';
import { HandleErrors } from './HandleErrors';
import { checkCookiebot } from '@/utilities/checkCookiebot';
import { LoginPage, PleaseWait } from '@/components';
import { useNotificationStore } from '@/stores/notificationstore';
import { clearUser, identifyUser } from '@/integrations/sentry/errorReporting';
import ResetPasswordPage from '@/components/login/ResetPasswordPage';
import ChangePasswordPage from '@/components/login/ChangePasswordPage';
import { Layout } from './Layout';
import { getAdditionalElements, popupDefs } from './popups';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { MinimalUser } from '@/generated/model';
import { RouteErrorBoundary } from '@/app/routing/route-error-boundary/RouteErrorBoundary';

async function closePopup() {
  // itt nincs route context :(
  history.back();
}

function getPopups(isPhone: boolean, user: MinimalUser) {
  const popups = popupDefs({
    closePopup,
    isBottom: isPhone,
    user,
  });

  // children csak a menustruktura szempontjabol szamit
  // route definicional ugyanolyan
  let allChildren: PopupType[] = []; // most csak 1 melysegu, de lehetne rekurziv
  popups.forEach((p) => {
    if (p.children?.length) allChildren = allChildren.concat(p.children);
  });

  const additionalElement = getAdditionalElements(closePopup, true, true, isPhone);
  additionalElement.forEach((p) => {
    if (p.children?.length) allChildren = allChildren.concat(p.children);
  });

  const allPopups = popups.concat(additionalElement).concat(allChildren);
  return { popups, allPopups };
}

function RenderLogin({
  loggedIn,
  pwReset,
  initialUsername,
}: {
  loggedIn: (user: UserState) => void;
  pwReset: () => void;
  initialUsername?: string;
}) {
  return (
    <HandleErrors>
      <LoginPage loggedIn={loggedIn} resetPw={pwReset} initialUsername={initialUsername} />
    </HandleErrors>
  );
}

function RenderApp({ user }: { user: MinimalUser }) {
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen());

  useEffect(() => {
    function handleResize() {
      if (document.readyState !== 'complete') {
        setTimeout(handleResize, 100);
        return;
      }
      setPhone(isPhoneOrHighScreen());
    }

    setTimeout(handleResize, 100);

    // TODO lehetne mediaquery
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { popups, allPopups } = useMemo(() => getPopups(isPhone, user), [isPhone, user]);

  function defaultRoute() {
    return isPhone ? '/tracking/select' : '/tracking';
  }

  function buildTree(level: number, prefix: string) {
    if (level >= 4) return null;

    function samePrefix(path: string) {
      if (!path.startsWith('/')) return true;
      return path.startsWith(prefix);
    }

    function getPath(path: string) {
      if (path.startsWith('/')) return path;
      return prefix + '/' + path;
    }

    function removeLeadingSlash(path: string) {
      if (path.startsWith('/')) return path.substring(1);
      return path;
    }

    function buildPrefix(prefix: string, path: string) {
      if (path.startsWith(prefix)) return path;
      return prefix + '/' + removeLeadingSlash(path);
    }

    const result = allPopups.filter((r) => r.level === level).filter((r) => samePrefix(r.path));

    return result.map((p) => {
      const path = getPath(p.path);

      return (
        <Route
          key={`${level}:${path}`}
          path={path}
          element={<RouteErrorBoundary routePath={path}>{p.element}</RouteErrorBoundary>}
        >
          {buildTree(level + 1, buildPrefix(prefix, p.path))}
        </Route>
      );
    });
  }

  return (
    <HandleErrors>
      <BrowserRouter basename={BASE}>
        <Routes>
          <Route path="/" element={<Layout isPhone={isPhone} popups={popups} />}>
            {buildTree(1, '')}
          </Route>
          <Route index element={<Navigate replace to={defaultRoute()} />} />
          <Route path="*" element={<Navigate replace to={defaultRoute()} />} />
        </Routes>
      </BrowserRouter>
    </HandleErrors>
  );
}

function App() {
  const params = new URLSearchParams(document.location.search);
  const [user, setUser] = useUserState(undefined, params);
  const [loginInitialUsername, setLoginInitialUsername] = useState<string | undefined>(undefined);
  const [trackingAllowed, setTrackingAllowed] = useState<boolean | undefined>(undefined);
  const setNotificationsAllowed = useNotificationStore((state) => state.setNotificationsAllowed);

  useEffect(() => {
    if (user === null) {
      return;
    }

    if (typeof user === 'object' && user !== null) {
      identifyUser(user.id, user.isTokenUser);
      return;
    }

    clearUser();
  }, [user]);

  useEffect(() => {
    function onCookiebotDialog() {
      const cooikebotDialog = document.getElementById('CybotCookiebotDialog');
      if (cooikebotDialog) {
        cooikebotDialog.remove();
      }
    }

    window.addEventListener('CookiebotOnAccept', onCookiebotDialog);
    window.addEventListener('CookiebotOnDecline', onCookiebotDialog);

    return () => {
      window.removeEventListener('CookiebotOnAccept', onCookiebotDialog);
      window.removeEventListener('CookiebotOnDecline', onCookiebotDialog);
    };
  }, []);

  useEffect(() => {
    window.allowNotifications = setNotificationsAllowed;
    return () => {
      window.allowNotifications = () => {};
    };
  }, [setNotificationsAllowed]);

  useEffect(() => {
    if (!window.userConsent && !window.flutter_inappwebview) {
      return;
    }

    window.updateUserConsent = function (allowed: string) {
      setTrackingAllowed(allowed === 'true');
    };

    function onCookiebotDialog() {
      if (!window.userConsent && !window.flutter_inappwebview) {
        return;
      }
      if (trackingAllowed) {
        return;
      }
      if (!checkCookiebot()) {
        return;
      }
      Cookiebot.hide();
    }

    window.addEventListener('CookiebotOnDialogDisplay', onCookiebotDialog);

    if (trackingAllowed === undefined) {
      if (window.userConsent) {
        window.userConsent.postMessage('');
      }
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler('userConsent', '');
      }
    } else {
      try {
        if (!checkCookiebot()) {
          return;
        }
        if (trackingAllowed) {
          if (!Cookiebot.hasResponse) {
            Cookiebot.show();
          }
        } else {
          if (Cookiebot.hasResponse) {
            Cookiebot.withdraw();
          } else {
            Cookiebot.hide();
          }
        }
      } catch (e) {
        console.error('Cannot update Cookiebot. TrackingAllowed = ', trackingAllowed, e);
      }
    }
    return () => {
      window.removeEventListener('CookiebotOnDialogDisplay', onCookiebotDialog);
    };
  }, [trackingAllowed]);

  function loggedIn(user: UserState) {
    setLoginInitialUsername(undefined);
    setUser(user);
  }

  function pwReset() {
    setUser('password_reset');
  }

  function userReset(username?: string) {
    setLoginInitialUsername(username);
    setUser(null);
    window.history.replaceState(null, document.title, '/');
  }

  switch (user) {
    case undefined:
      return <PleaseWait />;
    case null:
      return <RenderLogin loggedIn={loggedIn} pwReset={pwReset} initialUsername={loginInitialUsername} />;
    case 'token_failure':
      return <TokenFail />;
    case 'password_reset':
      return <ResetPasswordPage returnLoginPage={userReset} invitedUser={params.get('invitedUser') || undefined} />;
    case 'change_password':
      return (
        <ChangePasswordPage
          successfulChange={userReset}
          user={params.get('user') ?? ''}
          hash={params.get('hash') ?? ''}
        />
      );
    default:
      return (
        <ctxUser.Provider value={createUserContextValue(user, () => setUser(null))}>
          <RenderApp user={user} />
        </ctxUser.Provider>
      );
  }
}

export default App;
